import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { DateFormatPipe } from '../../../../../shared/pipes';
import { TestAttemptService } from '../../../../../core/data-services/test-attempt.service';

interface ReportQuestion {
  id: string;
  questionNumber: number;
  question: string;
  options: { id: string; text: string }[];
  correctOptionId: string;
  userOptionId: string;
  isCorrect: boolean;
  category: string;
  explanation?: string;
}

interface ReportAttempt {
  id: string;
  date: string;
  score: number;
  passingScore: number;
  passed: boolean;
  duration: string;
  questionsCorrect: number;
  totalQuestions: number;
}

interface TestReport {
  testId: string;
  testTitle: string;
  shortName: string;
  description: string;
  icon: string;
  status: 'passed' | 'failed' | 'expired';
  currentAttempt: ReportAttempt;
  previousAttempts: ReportAttempt[];
  questions: ReportQuestion[];
  categoryBreakdown: { category: string; correct: number; total: number; percentage: number }[];
  certificateNumber?: string;
  expirationDate?: string;
}

@Component({
  selector: 'app-safety-test-report',
  standalone: true,
  imports: [CommonModule, RouterLink, DateFormatPipe],
  templateUrl: './safety-test-report.component.html',
  styleUrls: ['./safety-test-report.component.scss']
})
export class SafetyTestReportComponent implements OnInit {
  private testAttemptService = inject(TestAttemptService);

  isLoading = false;
  error: string | null = null;
  testId: string | null = null;
  attemptId: string | null = null;
  report: TestReport | null = null;
  showAllQuestions = false;
  showOnlyIncorrect = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.testId = this.route.snapshot.paramMap.get('testId');
    // Also check for attemptId in query params
    this.attemptId = this.route.snapshot.queryParamMap.get('attemptId');

    if (this.attemptId) {
      this.loadReportByAttempt(this.attemptId);
    } else if (this.testId) {
      this.loadReportByTest(this.testId);
    }
  }

  /**
   * Load report by attempt ID (preferred - direct link from results)
   */
  private async loadReportByAttempt(attemptId: string): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const results = await this.testAttemptService.getResults(attemptId);

      if (!results) {
        this.error = 'Could not load test results';
        return;
      }

      this.buildReportFromResults(results);
    } catch (error: any) {
      console.error('Error loading report:', error);
      this.error = error.message || 'Failed to load report';
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Load report by test ID (find most recent completed attempt)
   */
  private async loadReportByTest(testId: string): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      // Get user's attempts and find the most recent completed one for this test
      const attempts = await this.testAttemptService.getMyAttempts();
      const testAttempts = attempts
        .filter(a => a.testId === testId && (a.status === 'completed' || a.status === 'timed_out'))
        .sort((a, b) => new Date(b.completedAt || 0).getTime() - new Date(a.completedAt || 0).getTime());

      if (testAttempts.length === 0) {
        this.error = 'No completed attempts found for this test';
        return;
      }

      // Load results for the most recent attempt
      const latestAttempt = testAttempts[0];
      const results = await this.testAttemptService.getResults(latestAttempt.id);

      if (!results) {
        this.error = 'Could not load test results';
        return;
      }

      this.buildReportFromResults(results, testAttempts.slice(1));
    } catch (error: any) {
      console.error('Error loading report:', error);
      this.error = error.message || 'Failed to load report';
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Build report structure from API results
   */
  private buildReportFromResults(results: any, previousAttempts: any[] = []): void {
    const attempt = results.attempt;
    const test = results.test;
    const questions = results.questions || [];

    // Format time spent
    const timeSpentSeconds = attempt.timeSpentSeconds || 0;
    const minutes = Math.floor(timeSpentSeconds / 60);
    const seconds = timeSpentSeconds % 60;
    const duration = minutes > 0 ? `${minutes} min ${seconds} sec` : `${seconds} sec`;

    // Build questions with correct/incorrect info
    const reportQuestions: ReportQuestion[] = questions.map((q: any, idx: number) => {
      const options = (q.options || []).map((o: any) => ({
        id: o.id,
        text: o.text || o.optionText || o.option_text
      }));

      // Find the correct option ID from the options array (look for isCorrect: true)
      const correctOption = (q.options || []).find((o: any) => o.isCorrect || o.is_correct);
      const correctOptionId = correctOption?.id || q.correctOptionId || q.correct_option_id;

      return {
        id: q.id,
        questionNumber: idx + 1,
        question: q.text || q.questionText || q.question_text,
        options,
        correctOptionId,
        // Backend returns userAnswer (the selected option ID) and userCorrect (boolean)
        userOptionId: q.userAnswer || q.userOptionId || q.user_option_id || q.selectedOptionId || q.selected_option_id,
        isCorrect: q.userCorrect ?? q.isCorrect ?? q.is_correct ?? false,
        category: q.category || 'General',
        explanation: q.explanation
      };
    });

    // Calculate category breakdown
    const categoryMap = new Map<string, { correct: number; total: number }>();
    for (const q of reportQuestions) {
      const cat = q.category || 'General';
      const existing = categoryMap.get(cat) || { correct: 0, total: 0 };
      existing.total++;
      if (q.isCorrect) existing.correct++;
      categoryMap.set(cat, existing);
    }

    const categoryBreakdown = Array.from(categoryMap.entries()).map(([category, stats]) => ({
      category,
      correct: stats.correct,
      total: stats.total,
      percentage: Math.round((stats.correct / stats.total) * 100)
    }));

    // Build previous attempts
    const formattedPreviousAttempts: ReportAttempt[] = previousAttempts.map(a => ({
      id: a.id,
      date: a.completedAt || a.startedAt,
      score: a.score || 0,
      passingScore: test.passingScore || 70,
      passed: a.passed || false,
      duration: this.formatDuration(a.timeSpentSeconds || 0),
      questionsCorrect: a.correctAnswers || 0,
      totalQuestions: a.totalQuestions || 0
    }));

    // Generate certificate number for passed tests
    const certificateNumber = attempt.passed
      ? `ADN-${test.id?.substring(0, 4).toUpperCase() || 'TEST'}-${new Date(attempt.completedAt).getFullYear()}-${attempt.id?.substring(0, 8).toUpperCase()}`
      : undefined;

    this.report = {
      testId: test.id,
      testTitle: test.title,
      shortName: test.shortName || test.title,
      description: test.description || '',
      icon: test.icon || 'bi-clipboard-check',
      status: attempt.passed ? 'passed' : 'failed',
      currentAttempt: {
        id: attempt.id,
        date: attempt.completedAt || attempt.startedAt,
        score: attempt.score || 0,
        passingScore: test.passingScore || 70,
        passed: attempt.passed || false,
        duration,
        questionsCorrect: attempt.correctAnswers || 0,
        totalQuestions: attempt.totalQuestions || 0
      },
      previousAttempts: formattedPreviousAttempts,
      questions: reportQuestions,
      categoryBreakdown,
      certificateNumber,
      expirationDate: attempt.expiresAt
    };
  }

  private formatDuration(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins} min ${secs} sec` : `${secs} sec`;
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'passed': return 'badge-passed';
      case 'failed': return 'badge-failed';
      case 'expired': return 'badge-expired';
      default: return 'bg-secondary';
    }
  }

  getStatusText(status: string): string {
    switch (status) {
      case 'passed': return 'Passed';
      case 'failed': return 'Failed';
      case 'expired': return 'Expired';
      default: return status;
    }
  }

  getScoreClass(score: number, passingScore: number): string {
    if (score >= passingScore) {
      return score >= 90 ? 'score-excellent' : 'score-passed';
    }
    return 'score-failed';
  }

  getScoreLabel(score: number): string {
    if (score >= 90) return 'Excellent';
    if (score >= 80) return 'Good';
    if (score >= 70) return 'Needs Improvement';
    return 'Below Standard';
  }

  getCategoryBarClass(percentage: number): string {
    if (percentage >= 90) return 'bg-success';
    if (percentage >= 70) return 'bg-primary';
    if (percentage >= 50) return 'bg-warning';
    return 'bg-danger';
  }

  get displayedQuestions(): ReportQuestion[] {
    if (!this.report) return [];
    if (this.showOnlyIncorrect) {
      return this.report.questions.filter(q => !q.isCorrect);
    }
    return this.report.questions;
  }

  get incorrectCount(): number {
    if (!this.report) return 0;
    return this.report.questions.filter(q => !q.isCorrect).length;
  }

  toggleQuestionFilter(): void {
    this.showOnlyIncorrect = !this.showOnlyIncorrect;
  }

  downloadCertificate(): void {
    console.log('Download certificate');
    // TODO: Generate and download PDF certificate
  }

  printReport(): void {
    window.print();
  }

  retakeTest(): void {
    if (this.report) {
      this.router.navigate(['/logbook/testing/safety', this.report.testId, 'take']);
    }
  }

  goBack(): void {
    this.router.navigate(['/logbook/testing/safety']);
  }
}
